import React, { useEffect, useState } from "react";
import { Badge, Box, Button, Divider, HStack, Text, Tooltip, VStack } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import type { OrderSnapshot } from "../flow/types";
import { copyText, formatClock } from "./statusMeta";
import useNotification from "../../../utils/useNotification";

interface Props {
  snapshot?: OrderSnapshot;
  isBusy: boolean;
  onRefresh: () => Promise<void> | void;
  onViewOrder: (orderNo: string) => void;
}

const StatusValue: React.FC<{ value?: string; tone?: string }> = ({ value, tone = "gray" }) => (
  <AnimatePresence mode="popLayout" initial={false}>
    <motion.span
      key={value || "none"}
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      transition={{ duration: 0.2 }}
      style={{ display: "inline-block" }}
    >
      <Text as="span" fontFamily="mono" fontSize="xs" fontWeight="700" color={`${tone}.700`}>
        {value || "—"}
      </Text>
    </motion.span>
  </AnimatePresence>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <HStack justify="space-between" align="baseline" spacing={3}>
    <Text fontSize="xs" color="gray.500" flexShrink={0}>
      {label}
    </Text>
    <Box textAlign="right" minW={0}>
      {children}
    </Box>
  </HStack>
);

const toneFor = (status?: string) => {
  if (!status) return "gray";
  if (["CANCELLED", "SUPPLIER_CANCELLED", "ENQUIRY_CLOSED", "REVOKED"].includes(status)) return "red";
  if (["DELIVERED", "READY", "SHIPPED", "READY_TO_DISPATCH"].includes(status)) return "green";
  if (["CUSTOMER_HOLD", "BLOCKED", "CBO", "IN_REVIEW"].includes(status)) return "orange";
  if (["PART_CONVERSION_STARTED", "PART_CONVERSION_COMPLETED"].includes(status)) return "purple";
  if (["DRAFT_ESTIMATE_SHARED", "FINAL_ESTIMATE_REQUESTED"].includes(status)) return "teal";
  return "blue";
};

const OrderNo: React.FC<{ orderNo: string; onView: () => void }> = ({ orderNo, onView }) => {
  const toast = useNotification();
  return (
    <HStack spacing={1} justify="flex-end">
      <Tooltip label="Copy order number">
        <Text
          fontFamily="mono"
          fontSize="xs"
          fontWeight="700"
          cursor="pointer"
          onClick={async () => {
            await copyText(orderNo);
            toast({ status: "success", description: `${orderNo} copied` });
          }}
        >
          {orderNo}
        </Text>
      </Tooltip>
      <Button size="xs" variant="link" colorScheme="blue" onClick={onView}>
        view
      </Button>
    </HStack>
  );
};

const CurrentStateCard: React.FC<Props> = ({ snapshot, isBusy, onRefresh, onViewOrder }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [ago, setAgo] = useState("");
  useEffect(() => {
    const tick = () => {
      if (!snapshot) return setAgo("");
      const s = Math.round((Date.now() - snapshot.fetchedAt) / 1000);
      setAgo(s < 2 ? "just now" : `${s}s ago`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [snapshot]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
      <HStack justify="space-between" mb={3}>
        <HStack>
          <Text fontWeight="700" fontSize="sm">
            Current order state
          </Text>
          {snapshot && (
            <Text fontSize="10px" color="gray.400" fontFamily="mono">
              {formatClock(snapshot.fetchedAt)} · {ago}
            </Text>
          )}
        </HStack>
        <Button size="xs" variant="ghost" onClick={refresh} isLoading={refreshing} isDisabled={!snapshot || isBusy}>
          Refresh
        </Button>
      </HStack>
      {!snapshot?.enquiry ? (
        <Text fontSize="xs" color="gray.500">
          No order created yet. State is fetched live from the backend after every step and each polling check.
        </Text>
      ) : (
        <VStack align="stretch" spacing={2}>
          <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
            Enquiry
          </Text>
          <Row label="Order no.">
            <OrderNo orderNo={snapshot.enquiry.orderNo} onView={() => onViewOrder(snapshot.enquiry!.orderNo)} />
          </Row>
          <Row label="Status">
            <StatusValue value={snapshot.enquiry.orderStatus} tone={toneFor(snapshot.enquiry.orderStatus)} />
          </Row>
          <Row label="Test order">
            <Badge colorScheme={snapshot.enquiry.isTestOrder ? "orange" : "red"} fontSize="9px">
              {snapshot.enquiry.isTestOrder ? "isTestOrder=true" : "NOT FLAGGED"}
            </Badge>
          </Row>
          <Row label="Part conversion">
            <StatusValue value={snapshot.enquiry.partConversion} tone={snapshot.enquiry.partConversion === "COMPLETED" ? "green" : "gray"} />
          </Row>
          <Row label="Estimate shared">
            <StatusValue value={snapshot.enquiry.estimateSharedAt ? "yes" : "no"} tone={snapshot.enquiry.estimateSharedAt ? "green" : "gray"} />
          </Row>

          {snapshot.partConfirmation && (
            <>
              <Divider />
              <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
                Part confirmation
              </Text>
              <Row label="Request">
                <StatusValue value={snapshot.partConfirmation.status || (snapshot.partConfirmation.requestId ? "open" : "none")} />
              </Row>
              <Row label="Responded">
                <StatusValue
                  value={`${snapshot.partConfirmation.confirmedParts}/${snapshot.partConfirmation.totalParts} confirmed`}
                  tone={snapshot.partConfirmation.responded ? "green" : "gray"}
                />
              </Row>
            </>
          )}

          {snapshot.estimate && (
            <>
              <Divider />
              <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
                Estimate
              </Text>
              <Row label="Estimate no.">
                <StatusValue value={snapshot.estimate.estimateNo} />
              </Row>
              <Row label="Status">
                <StatusValue value={snapshot.estimate.status} tone={toneFor(snapshot.estimate.status)} />
              </Row>
              <Row label="Split">
                <StatusValue value={`${snapshot.estimate.splitStatus || "—"} · ${snapshot.estimate.transactionType || "—"}`} />
              </Row>
            </>
          )}

          {snapshot.child && (
            <>
              <Divider />
              <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
                Child order
              </Text>
              <Row label="Order no.">
                <OrderNo orderNo={snapshot.child.orderNo} onView={() => onViewOrder(snapshot.child!.orderNo)} />
              </Row>
              <Row label="Garaaz status">
                <StatusValue value={snapshot.child.orderStatus} tone={toneFor(snapshot.child.orderStatus)} />
              </Row>
              <Row label="Supplier status">
                <StatusValue value={snapshot.child.supplierStatus} tone={toneFor(snapshot.child.supplierStatus)} />
              </Row>
              <Row label="Supplier invoice">
                <StatusValue value={snapshot.child.supplierInvoiceNo || "none"} tone={snapshot.child.supplierInvoiceNo ? "green" : "gray"} />
              </Row>
              <Row label="Garaaz invoice">
                <StatusValue value={snapshot.child.hasGaraazInvoice ? snapshot.child.garaazInvoiceNo || "yes" : "none"} tone={snapshot.child.hasGaraazInvoice ? "green" : "gray"} />
              </Row>
              <Row label="Transaction">
                <StatusValue value={snapshot.child.transactionType} />
              </Row>
              <Row label="Order ETA">
                <StatusValue value={snapshot.child.hasOrderETA ? "set" : "missing"} tone={snapshot.child.hasOrderETA ? "green" : "orange"} />
              </Row>
              <Row label="Shipment">
                <StatusValue value={snapshot.child.shipmentId || "none"} />
              </Row>
              <Row label="Rider">
                <StatusValue value={snapshot.child.dispatchRider || "—"} />
              </Row>
            </>
          )}

          {snapshot.shipment && (
            <>
              <Divider />
              <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
                Shipment
              </Text>
              <Row label="Status">
                <StatusValue value={snapshot.shipment.status} tone={toneFor(snapshot.shipment.status)} />
              </Row>
              <Row label="Mode">
                <StatusValue value={snapshot.shipment.dispatchMode} />
              </Row>
              <Row label="Rider">
                <StatusValue value={snapshot.shipment.riderName} />
              </Row>
            </>
          )}
        </VStack>
      )}
    </Box>
  );
};

export default CurrentStateCard;
